// src/components/plaid/LinkedAccountsCard.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Panel,
  cn,
} from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { mountStarsIcons } from "../../styles/stars-icons";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { hasChatAccess } from "../../shared/_helpers/chatGate";
import {
  getPlaidLinks,
  unlinkPlaidLink,
} from "../../shared/_services/plaid.services";
import { GENERIC_ERROR, TRY_AGAIN } from "../onboarding/onboardingCopy";
import PlaidConsentRow from "./PlaidConsentRow";
import { usePlaidOffer } from "./usePlaidOffer";
import {
  LINKED_ANCHOR,
  PROFILE_PATH,
  SEE_FIGURES_PATH,
  announceLinksChanged,
  onLinksChanged,
} from "./plaidNav";
import {
  LINKED_FOOT,
  LINKED_LEDE,
  LINKED_NONE,
  LINKED_ON,
  LINKED_TITLE,
  LINKS_LOAD_FAILED,
  LINK_ANOTHER,
  LINK_FAILED,
  LINK_FIRST,
  NEEDS_ATTENTION,
  RECONNECT,
  RECONNECT_NAMED,
  SEE_FIGURES,
  UNLINK,
  UNLINKED,
  UNLINKING,
  UNLINK_BODY,
  UNLINK_CANCEL,
  UNLINK_CONFIRM,
  UNLINK_FAILED,
  UNLINK_NAMED,
  UNLINK_TITLE,
  accountsLine,
  sharedNames,
} from "./plaidCopy";

const ICON = "tw-h-5 tw-w-5 tw-shrink-0";

// Profile › Linked accounts (spec § Screens per app → Web; prototype
// profile.html:93-106), in Profile's legacy `ast-card` look like
// ConsentsCard (Decided 8, owner item 7); the Unlink confirm is the
// foundation ui/Dialog. Data: GET /plaid/links. Seeing and unlinking are never
// gated (owner item 3); linking a new bank here — Link an account, Link
// another account, Reconnect — shows only with chat access (R309), read from
// the stored user as the rest of Profile is (SP5 R255). A Plaid or vault
// outage still answers "unlinked" (owner item 4); only a failed request says
// "We couldn’t unlink …".
export default function LinkedAccountsCard() {
  const { hash } = useLocation();
  const section = useRef(null);
  const heading = useRef(null);
  const [load, setLoad] = useState({
    status: "loading",
    consent: false,
    links: [],
  });
  const [unlinking, setUnlinking] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [status, setStatus] = useState("");
  const [unlinkError, setUnlinkError] = useState(null);
  const inFlight = useRef(new Set());
  // Unlinked ids: a slower reply or re-read must not bring a removed row back.
  const gone = useRef(new Set());
  const kept = (links) => links.filter((l) => !gone.current.has(l.linkId));
  const returnFocus = useRef(null);
  const arrived = useRef(false);
  const alive = useRef(false);
  const canLink = hasChatAccess(readCurrentUser()?.user);

  useEffect(() => {
    alive.current = true;
    mountStarsIcons();
    return () => {
      alive.current = false;
    };
  }, []);
  const read = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoad((l) => ({ ...l, status: "loading" }));
    try {
      const res = await getPlaidLinks();
      if (alive.current)
        setLoad({
          status: "ready",
          consent: res.data.consent,
          links: kept(res.data.links),
        });
    } catch (_) {
      if (alive.current) setLoad((l) => ({ ...l, status: "error" }));
    }
  }, []);
  useEffect(() => {
    read();
  }, [read]);
  // A link made here, or Plaid switched off in Settings › Consents.
  useEffect(() => onLinksChanged(() => read({ quiet: true })), [read]);
  // /profile#linked (the Questions row's Manage, the header's Linked
  // accounts): once drawn, the panel scrolls into view and takes focus.
  useEffect(() => {
    if (hash !== `#${LINKED_ANCHOR}`) {
      arrived.current = false;
      return;
    }
    if (load.status === "loading" || arrived.current) return;
    arrived.current = true;
    section.current?.scrollIntoView();
    heading.current?.focus();
  }, [hash, load.status]);

  const offer = usePlaidOffer({
    redirectPath: PROFILE_PATH,
    consent: load.consent,
    // Heard on a consent given here and again after the exchange, so
    // Settings › Consents follows even if Link is closed without linking.
    onStatus: () => announceLinksChanged(),
    onLinked: () => null,
  });

  const ask = (link, button) => {
    returnFocus.current = button;
    setConfirming(link);
  };
  const unlink = async (link) => {
    setConfirming(null);
    if (inFlight.current.has(link.linkId)) return;
    inFlight.current.add(link.linkId);
    setUnlinking((ids) => [...ids, link.linkId]);
    setUnlinkError(null);
    setStatus("");
    const name = link.institution.name;
    try {
      const res = await unlinkPlaidLink(link.linkId);
      if (!alive.current) return;
      gone.current.add(link.linkId);
      setLoad((l) => ({ ...l, links: kept(res.data.links) }));
      setStatus(UNLINKED(name));
    } catch (_) {
      if (alive.current) setUnlinkError(UNLINK_FAILED(name));
    } finally {
      inFlight.current.delete(link.linkId);
      if (alive.current)
        setUnlinking((ids) => ids.filter((id) => id !== link.linkId));
    }
  };

  const shared = sharedNames(load.links);

  const MUTED = "tw-m-0 tw-text-sm tw-text-text-muted";
  const LIST = "tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0";
  const ROW =
    "tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-md tw-bg-surface-sunken tw-p-3";
  const ERROR = "tw-mb-0 tw-mt-3 tw-text-sm tw-text-danger";

  return (
    <section id={LINKED_ANCHOR} ref={section} aria-labelledby="linked-h">
      <Panel as="div">
        <h2
          id="linked-h"
          ref={heading}
          tabIndex={-1}
          className="tw-m-0 tw-mb-2 tw-text-lg tw-font-bold tw-text-text focus:tw-outline-none"
        >
          {LINKED_TITLE}
        </h2>
        <p className={cn(MUTED, "tw-mb-4")}>{LINKED_LEDE}</p>

        {load.status === "loading" && (
          <ul className={LIST} aria-busy="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className={ROW} aria-hidden="true">
                <span className="tw-block tw-h-4 tw-w-1/2 tw-rounded-sm tw-bg-surface-selected" />
              </li>
            ))}
          </ul>
        )}

        {load.status === "error" && (
          <div role="alert" className="tw-text-sm">
            <span>{LINKS_LOAD_FAILED}</span>{" "}
            <Button variant="text" onClick={() => read()}>
              {TRY_AGAIN}
            </Button>
          </div>
        )}

        {load.status === "ready" && load.links.length === 0 && (
          <>
            <p className={cn(MUTED, "tw-mb-4")}>{LINKED_NONE}</p>
            {canLink && <Button onClick={offer.start}>{LINK_FIRST}</Button>}
          </>
        )}

        {load.status === "ready" && load.links.length > 0 && (
          <>
            <ul className={LIST}>
              {load.links.map((link) => {
                const name = link.institution.name;
                const busy = unlinking.includes(link.linkId);
                const metaId = `linked-${link.linkId}-meta`;
                return (
                  <li key={link.linkId} className={ROW}>
                    <StarsIcon
                      name={
                        link.institution.kind === "investments"
                          ? "chart"
                          : "bank"
                      }
                      className={ICON}
                    />
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-font-bold">{name}</div>
                      <div id={metaId} className={MUTED}>
                        {accountsLine(link.accounts, shared.has(name))}
                      </div>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                      {link.status === "needs_attention" ? (
                        <>
                          <Badge tone="warning">{NEEDS_ATTENTION}</Badge>
                          {canLink && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={RECONNECT_NAMED(name)}
                              aria-describedby={metaId}
                              onClick={offer.start}
                            >
                              {RECONNECT}
                            </Button>
                          )}
                        </>
                      ) : (
                        link.linkedAt && (
                          <span className={cn(MUTED, "tw-tabular-nums")}>
                            {LINKED_ON(link.linkedAt)}
                          </span>
                        )
                      )}
                      <Button
                        variant="quiet"
                        size="sm"
                        aria-label={busy ? undefined : UNLINK_NAMED(name)}
                        aria-describedby={metaId}
                        disabled={busy}
                        onClick={(e) => ask(link, e.currentTarget)}
                      >
                        {busy ? UNLINKING : UNLINK}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-3">
              <Button as={Link} to={SEE_FIGURES_PATH} variant="quiet">
                {SEE_FIGURES}
              </Button>
              {canLink && (
                <Button variant="ghost" onClick={offer.start}>
                  <StarsIcon
                    name="plus"
                    className="tw-h-4 tw-w-4 tw-shrink-0"
                  />
                  {LINK_ANOTHER}
                </Button>
              )}
            </div>
          </>
        )}

        {offer.consentRow && (
          <div className="tw-mt-3">
            <PlaidConsentRow
              row={offer.consentRow}
              onConsent={offer.giveConsent}
              idPrefix="linked"
            />
          </div>
        )}
        {offer.failed && (
          <p role="alert" className={ERROR}>
            {offer.failed === "plaid" ? LINK_FAILED : GENERIC_ERROR}
          </p>
        )}
        {unlinkError && (
          <p role="alert" className={ERROR}>
            {unlinkError}
          </p>
        )}
        <p role="status" className={cn(MUTED, "tw-mt-3")}>
          {status}
        </p>
        <p className="tw-mb-0 tw-mt-4 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-4 tw-text-xs tw-text-text-muted">
          {LINKED_FOOT}
        </p>
      </Panel>

      <Dialog
        open={Boolean(confirming)}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        {confirming && (
          <DialogContent
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              const back = returnFocus.current;
              (back?.isConnected && !back.disabled
                ? back
                : heading.current
              )?.focus();
            }}
          >
            <DialogTitle>
              {UNLINK_TITLE(confirming.institution.name)}
            </DialogTitle>
            <DialogDescription>
              {UNLINK_BODY(
                confirming.institution.name,
                shared.has(confirming.institution.name)
                  ? accountsLine(confirming.accounts, true)
                  : null,
              )}
            </DialogDescription>
            <DialogActions>
              <DialogClose asChild>
                <Button variant="quiet">{UNLINK_CANCEL}</Button>
              </DialogClose>
              <Button variant="danger" onClick={() => unlink(confirming)}>
                {UNLINK_CONFIRM}
              </Button>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
